import type { BrandingConfig } from '@/config/branding';

/**
 * Monta o manifesto do PWA em tempo de EXECUÇÃO, a partir da marca.
 *
 * Por que não basta o manifesto do `vite.config.ts`: ele é gravado no build, e
 * o build é UM SÓ para as três marcas — quem decide a marca é o `config.js` que
 * o contêiner escreve com `envsubst` ao subir. Com o manifesto estático, o
 * atalho instalado saía como "Whiz" mesmo em ProsaZap e Whiz Farma.
 *
 * O manifesto gerado substitui o `<link rel="manifest">` que o vite-plugin-pwa
 * injeta no `index.html`, apontando para um Blob.
 *
 * ⚠️ Todas as URLs precisam ser ABSOLUTAS. Um manifesto servido de `blob:` não
 * tem URL-base utilizável, e caminhos relativos (`/img/...`) seriam resolvidos
 * contra o próprio blob — os ícones sumiriam e o `start_url` apontaria para o
 * nada. `new URL(x, origin).href` resolve isso antes de serializar.
 */
export function applyPwaManifest(branding: BrandingConfig): void {
    // Documento sem `<head>` utilizável (ambiente de teste, SSR): sai sem erro.
    if (typeof document === 'undefined' || !document.head) return;

    const origem = window.location.origin;
    const absoluta = (caminho: string) => new URL(caminho, origem).href;

    const manifesto = {
        name: branding.companyName,
        short_name: branding.shortName,
        description: `${branding.companyName} - Plataforma de atendimento`,
        theme_color: branding.themeColor,
        background_color: branding.themeColor,
        display: 'standalone',
        orientation: 'portrait',
        scope: absoluta('/'),
        start_url: absoluta('/'),
        icons: branding.pwaIcons.map((icone) => ({
            ...icone,
            src: absoluta(icone.src),
        })),
    };

    let link = document.querySelector<HTMLLinkElement>('link[rel="manifest"]');

    if (!link) {
        link = document.createElement('link');
        link.rel = 'manifest';
        document.head.appendChild(link);
    }

    // O blob anterior (se esta função rodar duas vezes) fica retido pelo
    // navegador até ser revogado; sem isto, cada chamada vaza alguns KB.
    const anterior = link.dataset.brandingBlob;
    if (anterior) URL.revokeObjectURL(anterior);

    const blob = new Blob([JSON.stringify(manifesto)], { type: 'application/manifest+json' });
    const url = URL.createObjectURL(blob);

    link.href = url;
    link.dataset.brandingBlob = url;
}
